import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Search, X, ChevronDown } from 'lucide-react';

interface SearchFilters {
  query: string;
  software: string[];
  holiday: string[];
  genres: string[];
  artists: string[];
  creators?: string[];
  minPrice?: number;
  maxPrice?: number;
  featured?: boolean;
  isFree?: boolean;
  fileFormats?: string[];
  albums?: string[];
  sort: string;
}

interface SequenceFilterSidebarProps {
  filters: SearchFilters;
  facets: any;
  facetsExpanded: Record<string, boolean>;
  artistSearchQuery: string;
  creatorSearchQuery?: string;
  albumSearchQuery?: string;
  onFiltersChange: (filters: SearchFilters) => void;
  onToggleArrayFilter: (filterType: keyof Pick<SearchFilters, 'software' | 'holiday' | 'genres' | 'artists' | 'creators'>, value: string) => void;
  onToggleExtraArrayFilter?: (filterType: keyof Pick<SearchFilters, 'fileFormats' | 'albums'>, value: string) => void;
  onToggleBooleanFilter: (filterType: 'featured' | 'isFree') => void;
  onSetPriceRange: (min?: number, max?: number) => void;
  onClearAllFilters: () => void;
  onToggleFacetsExpanded: (facet: string) => void;
  onArtistSearchChange: (query: string) => void;
  onCreatorSearchChange?: (query: string) => void;
  onAlbumSearchChange?: (query: string) => void;
}

const FilterSection: React.FC<{
  title: string;
  expanded: boolean;
  onToggle: () => void;
  items: Array<{ value: string; count: number }>;
  selectedItems: string[];
  onToggleItem: (value: string) => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  showSearch?: boolean;
}> = ({ title, expanded, onToggle, items, selectedItems, onToggleItem, searchQuery, onSearchChange, showSearch = false }) => {
  const filteredItems = showSearch && searchQuery 
    ? items.filter(item => item.value.toLowerCase().includes(searchQuery.toLowerCase()))
    : items;

  if (showSearch) {
    return (
      <Collapsible open={expanded} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between p-2 h-auto">
            <span className="font-medium">{title}</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-2 p-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={`Search ${title.toLowerCase()}...`}
              className="pl-8 h-8"
              value={searchQuery || ''}
              onChange={(e) => onSearchChange?.(e.target.value)}
            />
          </div>
          <ScrollArea className="max-h-48">
            {filteredItems.length > 0 ? (
              filteredItems.map((item) => (
                <div key={item.value} className="flex items-center space-x-2 py-1">
                  <Checkbox
                    id={item.value}
                    checked={selectedItems.includes(item.value)}
                    onCheckedChange={() => onToggleItem(item.value)}
                    className="shrink-0"
                  />
                  <label 
                    htmlFor={item.value} 
                    className="text-sm cursor-pointer grow min-w-0"
                  >
                    <span className="truncate block">{item.value}</span>
                    <span className="text-xs text-muted-foreground">
                      ({item.count.toLocaleString()})
                    </span>
                  </label>
                </div>
              ))
            ) : (
              <div className="text-sm text-muted-foreground py-2 text-center">
                No {title.toLowerCase()} found
              </div>
            )}
          </ScrollArea>
        </CollapsibleContent>
      </Collapsible>
    );
  }

  return (
    <Collapsible open={expanded} onOpenChange={onToggle}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="w-full justify-between p-2 h-auto">
          <span className="font-medium">{title}</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2 p-2">
        <ScrollArea className="max-h-48">
          {items.map((item) => (
            <div key={item.value} className="flex items-center space-x-2 py-1">
              <Checkbox
                id={item.value}
                checked={selectedItems.includes(item.value)}
                onCheckedChange={() => onToggleItem(item.value)}
                className="shrink-0"
              />
              <label 
                htmlFor={item.value} 
                className="text-sm cursor-pointer grow min-w-0"
              >
                <span className="truncate block">{item.value}</span>
                <span className="text-xs text-muted-foreground">
                  ({item.count.toLocaleString()})
                </span>
              </label>
            </div>
          ))}
        </ScrollArea>
      </CollapsibleContent>
    </Collapsible>
  );
};

export const SequenceFilterSidebar: React.FC<SequenceFilterSidebarProps> = ({
  filters,
  facets,
  facetsExpanded,
  artistSearchQuery,
  creatorSearchQuery,
  albumSearchQuery,
  onToggleArrayFilter,
  onToggleExtraArrayFilter,
  onToggleBooleanFilter,
  onSetPriceRange,
  onClearAllFilters,
  onToggleFacetsExpanded,
  onArtistSearchChange,
  onCreatorSearchChange,
  onAlbumSearchChange,
}) => {
  // Determine if any filters are currently active to control Clear All visibility
  const hasActiveFilters = (
    (filters.software && filters.software.length > 0) ||
    (filters.holiday && filters.holiday.length > 0) ||
    (filters.genres && filters.genres.length > 0) ||
    (filters.artists && filters.artists.length > 0) ||
    (filters.creators && filters.creators.length > 0) ||
    (filters.fileFormats && filters.fileFormats.length > 0) ||
    (filters.albums && filters.albums.length > 0) ||
    filters.featured === true ||
    filters.isFree === true ||
    typeof filters.minPrice === 'number' ||
    typeof filters.maxPrice === 'number'
  );
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Filters</CardTitle>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={onClearAllFilters}>
              <X className="h-4 w-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-1">
        {/* Software Filter */}
        {facets?.software_type?.counts && facets.software_type.counts.length > 0 && (
          <FilterSection
            title="Software"
            expanded={facetsExpanded.software}
            onToggle={() => onToggleFacetsExpanded('software')}
            items={facets.software_type.counts}
            selectedItems={filters.software}
            onToggleItem={(value) => onToggleArrayFilter('software', value)}
          />
        )}

        {/* Holiday Filter */}
        {facets?.holiday_type?.counts && facets.holiday_type.counts.length > 0 && (
          <FilterSection
            title="Holiday"
            expanded={facetsExpanded.holiday}
            onToggle={() => onToggleFacetsExpanded('holiday')}
            items={facets.holiday_type.counts}
            selectedItems={filters.holiday}
            onToggleItem={(value) => onToggleArrayFilter('holiday', value)}
          />
        )}

        {/* Genre Filter */}
        {facets?.genres?.counts && facets.genres.counts.length > 0 && (
          <FilterSection
            title="Genre"
            expanded={facetsExpanded.genres}
            onToggle={() => onToggleFacetsExpanded('genres')}
            items={facets.genres.counts}
            selectedItems={filters.genres}
            onToggleItem={(value) => onToggleArrayFilter('genres', value)}
          />
        )}

        {/* Artist Filter with Search */}
        {facets?.artist?.counts && facets.artist.counts.length > 0 && (
          <FilterSection
            title="Artists"
            expanded={facetsExpanded.artists}
            onToggle={() => onToggleFacetsExpanded('artists')}
            items={facets.artist.counts}
            selectedItems={filters.artists}
            onToggleItem={(value) => onToggleArrayFilter('artists', value)}
            searchQuery={artistSearchQuery}
            onSearchChange={onArtistSearchChange}
            showSearch={true}
          />
        )}

        {/* Sequencers Filter with Search */}
        {facets?.creator_name?.counts && facets.creator_name.counts.length > 0 && (
          <FilterSection
            title="Sequencers"
            expanded={facetsExpanded.creators}
            onToggle={() => onToggleFacetsExpanded('creators')}
            items={facets.creator_name.counts}
            selectedItems={filters.creators || []}
            onToggleItem={(value) => onToggleArrayFilter('creators', value)}
            searchQuery={creatorSearchQuery}
            onSearchChange={onCreatorSearchChange}
            showSearch={true}
          />
        )}

        {/* Price Range Filter */}
        {facets?.price?.stats && (
          <Collapsible 
            open={facetsExpanded.price} 
            onOpenChange={() => onToggleFacetsExpanded('price')}
          >
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between p-2 h-auto">
                <span className="font-medium">Price Range</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${facetsExpanded.price ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="p-2 space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Min: ${filters.minPrice ?? facets.price.stats.min ?? 0}</span>
                  <span>Max: ${filters.maxPrice ?? facets.price.stats.max ?? 100}</span>
                </div>
                <Slider
                  min={facets.price.stats?.min ?? 0}
                  max={facets.price.stats?.max ?? 100}
                  step={1}
                  value={[filters.minPrice ?? facets.price.stats?.min ?? 0, filters.maxPrice ?? facets.price.stats?.max ?? 100]}
                  onValueChange={([min, max]) => onSetPriceRange(min, max)}
                  className="my-2"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>${facets.price.stats?.min ?? 0}</span>
                  <span>${facets.price.stats?.max ?? 100}</span>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* File Format Filter */}
        {facets?.file_type?.counts && facets.file_type.counts.length > 0 && (
          <FilterSection
            title="File Format"
            expanded={facetsExpanded.other}
            onToggle={() => onToggleFacetsExpanded('other')}
            items={facets.file_type.counts}
            selectedItems={filters.fileFormats || []}
            onToggleItem={(value) => onToggleExtraArrayFilter?.('fileFormats', value)}
          />
        )}

        {/* Album Filter with Search */}
        {facets?.album?.counts && facets.album.counts.length > 0 && (
          <FilterSection
            title="Albums"
            expanded={facetsExpanded.albums}
            onToggle={() => onToggleFacetsExpanded('albums')}
            items={facets.album.counts}
            selectedItems={filters.albums || []}
            onToggleItem={(value) => onToggleExtraArrayFilter?.('albums', value)}
            searchQuery={albumSearchQuery}
            onSearchChange={onAlbumSearchChange}
            showSearch={true}
          />
        )}

        {/* Other Filters */}
        <Collapsible 
          open={facetsExpanded.other} 
          onOpenChange={() => onToggleFacetsExpanded('other')}
        >
          <CollapsibleTrigger asChild>
            <Button variant="ghost" className="w-full justify-between p-2 h-auto">
              <span className="font-medium">Other</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${facetsExpanded.other ? 'rotate-180' : ''}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="p-2 space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="featured"
                checked={filters.featured === true}
                onCheckedChange={() => onToggleBooleanFilter('featured')}
              />
              <label htmlFor="featured" className="text-sm cursor-pointer">
                Featured Only
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="free"
                checked={filters.isFree === true}
                onCheckedChange={() => onToggleBooleanFilter('isFree')}
              />
              <label htmlFor="free" className="text-sm cursor-pointer">
                Free Only
              </label>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
};